import React, { useEffect, useState } from "react";
import api from "../api/axios";

function OtherInvoice() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null); // Holds full row data
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateInvoices, setGenerateInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [submittingGenerate, setSubmittingGenerate] = useState(false);

  const token = localStorage.getItem("accessToken");
  const branchId = JSON.parse(sessionStorage.getItem("selectedContext"))?.branch;

  // ============== FETCH BATCH LIST ==============
  const fetchInvoiceBatches = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/other-invoice-list/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBatches(res.data.data || []);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceBatches();
  }, []);

  // ============== DOWNLOAD PDF ==============
  const handleDownload = async (batchId) => {
    try {
      const response = await api.get(`/download-other-invoice/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `other-invoice-${batchId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
      alert("Unable to download invoice.");
    }
  };

  // ============== OPEN FEEDBACK MODAL ==============
  const openFeedbackModal = (batch) => {
    setSelectedBatch(batch);
    setFeedback("");
    setShowModal(true);
  };

  // ============== SUBMIT FEEDBACK ==============
  const submitFeedback = async () => {
    if (!feedback.trim()) {
      alert("Please enter feedback.");
      return;
    }

    setSubmitting(true);

    try {
      await api.post(
        "/send-invoice-feedback",
        {
          invoice_batch_id: selectedBatch.id,
          feedback: feedback,
          branch_id: branchId,
          invoice_type: 'O'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Feedback submitted successfully.");
      setShowModal(false);

    } catch (err) {
      console.error("Feedback submission error:", err);
      alert("Failed to submit feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  const loadFeedbackHistory = async (invoiceBatchId) => {
    setHistoryLoading(true);
    setShowHistoryModal(true);

    try {
      const res = await api.get(`/other-invoice/${invoiceBatchId}/feedback-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.data;

      setFeedbackHistory(data.history || []);
    } catch (err) {
      console.error("Error loading history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const submitGenerate = async (batch) => {

    setSubmittingGenerate(true);

    try {
      await api.post(
        "/generate-other-invoice-journal-entries",
        {
          invoice_id: batch.id,
          branch_id: branchId,

          invoice:
          {
            id: batch.id,
            client_id: batch.client.user_id,
            invoice_no: batch.invoice_no,
            invoice_date: batch.invoice_date,
            total_amount: batch.total_amount,
            description: batch.description,

            // ▶ Include relation table data
            items: batch.items?.map(item => ({
              ledger_id: item.ledger_id,
              description: item.description,
              debit: 0,
              credit: item.amount
            })) || []
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert("Journal entries generated successfully.");
      setShowGenerateModal(false);
      fetchInvoiceBatches();

    } catch (error) {
      console.error(error);
      alert("Generation failed.");
    } finally {
      setSubmittingGenerate(false);
    }
  };

  return (
    <>
      {/* Page Header */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Other Invoice</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="#">Accounts</a></li>
                <li className="breadcrumb-item active">Other Invoice</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* List Card */}
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Invoice List</h4>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={fetchInvoiceBatches}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            <div className="card-body">
              {loading ? (
                <p>Loading...</p>
              ) : batches.length === 0 ? (
                <p className="text-muted">No other invoices found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Client</th>
                        <th>Invoice No</th>
                        <th>Invoice Date</th>
                        <th>Amount</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {batches.map((batch, index) => (
                        <tr key={index}>
                          <td>{batch.client.name}</td>
                          <td>{batch.invoice_no}</td>
                          <td>{new Date(batch.invoice_date).toLocaleDateString()}</td>
                          <td>{batch.total_amount}</td>
                          <td className="text-center">

                            {/* FEEDBACK */}
                            {0 == batch.finalize && (<button
                              className="btn btn-warning btn-sm me-2"
                              onClick={() => openFeedbackModal(batch)}
                            >
                              <i className="bi bi-chat-left-text me-1"></i> Send Feedback
                            </button>)}

                            <button
                              className="btn btn-outline-info btn-sm me-2"
                              onClick={() => loadFeedbackHistory(batch.id)}
                            >
                              History
                            </button>

                            {1 == batch.finalize && (<button
                              className="btn btn-success btn-sm me-2"
                              title="Journal generated for these invoices"
                            >
                              <i className="bi bi-chat-left-text me-1"></i> Completed
                            </button>)}

                            {/* DOWNLOAD */}
                            <button
                              className="btn btn-primary btn-sm me-2"
                              onClick={() => handleDownload(batch.id)}
                            >
                              <i className="bi bi-download me-1"></i> Download
                            </button>

                            {0 == batch.finalize && (
                              <button
                                className="btn btn-success btn-sm me-2"
                                onClick={() => submitGenerate(batch)}
                              >
                                {submittingGenerate ? "Generating..." : "Generate Entries"}
                              </button>
                            )}


                          </td>
                        </tr>
                      ))}
                    </tbody>

                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ================= FEEDBACK MODAL ================= */}
      {showModal && selectedBatch && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Send Invoice Feedback</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="modal-body">

                {/* DETAILS BOX */}
                <div className="border rounded p-2 mb-3 bg-light">

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Client:</div>
                    <div className="col-6 text-end">
                      {selectedBatch.client.name}
                    </div>
                  </div>

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Invoice No:</div>
                    <div className="col-6 text-end">
                      {selectedBatch.invoice_no}
                    </div>
                  </div>

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Invoice Date:</div>
                    <div className="col-6 text-end">
                      {new Date(selectedBatch.invoice_date).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Amount:</div>
                    <div className="col-6 text-end">
                      {selectedBatch.total_amount}
                    </div>
                  </div>
                </div>

                <label className="form-label">Feedback</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Enter your feedback..."
                ></textarea>

              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>

                <button
                  className="btn btn-primary"
                  onClick={submitFeedback}
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Submit Feedback"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ================= FEEDBACK HISTORY MODAL ================= */}
      {showHistoryModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Action & Feedback History</h5>
                <button className="btn-close" onClick={() => setShowHistoryModal(false)} />
              </div>

              <div className="modal-body">
                {historyLoading ? (
                  <p>Loading...</p>
                ) : feedbackHistory.length === 0 ? (
                  <p className="text-muted">No feedback submitted yet.</p>
                ) : (
                  <ul className="list-group">
                    {feedbackHistory.map((fb, index) => (
                      <li key={index} className="list-group-item">
                        <strong>Date:</strong>{" "}
                        {new Date(fb.created_at).toLocaleString("en-GB")}
                        <br />
                        <strong>Feedback:</strong> {fb.feedback}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default OtherInvoice;